/**
 * Recovery from a STALE BUILD.
 *
 * The app is installable (service worker), so a browser can keep running the
 * previous release's JavaScript after a deploy. When that old code meets the new
 * server — a field the server no longer sends, a chunk file that no longer
 * exists — it crashes (e.g. the Purchase dashboard reading `currencyChips`
 * after the dashboard was rebuilt).
 *
 * After a crash we ask the server for its current index.html. If its main
 * script differs from the one this page is running, this page is stale: drop
 * the service workers and caches and reload, once per build per tab.
 */

const scriptOf = (html: string): string => (html.match(/\/assets\/index-[A-Za-z0-9_-]+\.js/) || [''])[0];

function runningScript(): string {
  const el = Array.from(document.scripts).find((s) => /\/assets\/index-[A-Za-z0-9_-]+\.js/.test(s.src));
  return el ? scriptOf(el.src) : '';
}

async function clearAndReload(key: string) {
  try { sessionStorage.setItem('stale-build-reload', key); } catch { /* private mode */ }
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations?.()) || [];
    await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } catch { /* best effort */ }
  window.location.reload();
}

/** True when a reload was started (the caller should not render anything else). */
export async function recoverIfStale(): Promise<boolean> {
  try {
    const res = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return false;
    const latest = scriptOf(await res.text());
    const mine = runningScript();
    if (!latest || !mine || latest === mine) return false;
    // One attempt per new build per tab — never a reload loop.
    let tried = '';
    try { tried = sessionStorage.getItem('stale-build-reload') || ''; } catch { /* ignore */ }
    if (tried === latest) return false;
    await clearAndReload(latest);
    return true;
  } catch {
    return false;
  }
}

/** A lazy-loaded chunk of the old build was deleted by the deploy — same remedy. */
export function watchForStaleChunks() {
  window.addEventListener('vite:preloadError', (e) => {
    e.preventDefault();
    recoverIfStale().then((reloading) => { if (!reloading) window.location.reload(); });
  });
}
